import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

// IMPORTS
import { Dialog, Button, TextField, MenuItem } from "@mui/material";

// CSS
import styles from "./CompanyViewAddUserDialog.module.css";
import { GridSelectionModel } from "@mui/x-data-grid";
import { UserCompanyObject, addCompanyUser, getCompanyRoles, getCompanyUsers, removeCompanyUser } from "../companySlice";
import { CompanyRoleType } from "../../../types";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../app/store";

interface FormInterface {
  companyId: number;
  handleSave: any;
  handleClose: () => void;
  usersData: Array<UserCompanyObject>;
  add: boolean;
  edit: boolean;
  userToEdit?: UserCompanyObject[];
  gridSelectionModel: GridSelectionModel;
}

function validateEmail(email: string) {
  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

export default function CompanyViewAddUserDialog(props: FormInterface) {
  // COMMONS
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (props.companyId >= 0) {
      dispatch(getCompanyRoles());
    }else
      setErrorMessage(
        t("company_settings.add_dialog.error_false_companyid") + ", " + props.companyId
      );
  }, [props.companyId, dispatch, t]);

  // **********************************************************************************************************************
  // FORM DATA

  const companyRoles = useSelector((state: RootState) => state.company.companyRoles);

  const [userEmail, setuserEmail] = useState<string | undefined>(undefined);
  const [selectedRole, setselectedRole] = useState<CompanyRoleType | undefined>(undefined);

  const userToEdit = props.userToEdit !== undefined ? props.userToEdit[0] : null;

  //ERROR

  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (props.edit === true && userToEdit) {
      setselectedRole({id: userToEdit.companyRoleId.toString(), name: userToEdit.companyRoleName});
      setuserEmail(userToEdit.userName);
    }
    return () => {
      setselectedRole(undefined);
      setuserEmail(undefined);
    }
  }, [props.edit, userToEdit]);

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let selectedRole = companyRoles.find(x => x.id === event.target.value);
    setselectedRole(selectedRole);
  };

  const handleSave = async () => {
    if (props.usersData.filter(row => row.userName === userEmail).length > 0) {
      setErrorMessage(t("company_settings.add_dialog.user_already_exists"));
      return;
    }
    if (userEmail === undefined || selectedRole === undefined) {
      setErrorMessage(t("company_settings.add_dialog.please_fill_required"));
      return;
    } else if (props.companyId < 0) {
      setErrorMessage(
        t("company_settings.add_dialog.error_false_companyid") + ", " + props.companyId
      );
      return;
    } else if (validateEmail(userEmail) === false) {
      setErrorMessage(t("company_settings.add_dialog.error_email_not_valid"));
      return;
    }

    const timeNow = moment.now();

    const timeUtc = moment.utc(timeNow, true);

    let newUserDTO = {
      userName: userEmail,
      companyId: props.companyId,
      companyRoleId: Number(selectedRole.id),
      startDateTime: moment(timeUtc, true).format("YYYY-MM-DDTHH:mm:ss")
    };

    try {
      await dispatch(addCompanyUser({companyId: props.companyId, user: newUserDTO}));
    }
    catch (err) {
      console.log(err);
    }
    finally {
      dispatch(getCompanyUsers(props.companyId));
      props.handleSave();
    }
  };

  const handleClose = () => {
    props.handleClose();
  };

  const handleEditDialogSave = async (email: string | undefined, role: CompanyRoleType | undefined) => {
    if (props.usersData.filter(row => row.userName === email && row.id !== props.gridSelectionModel[0]).length > 0) {
      setErrorMessage(t("company_settings.add_dialog.user_already_exists"));
      return;
    }
    if (email === undefined || role === undefined) {
      setErrorMessage(t("company_settings.add_dialog.please_fill_required"));
      return;
    } else if (props.companyId < 0) {
      setErrorMessage(
        t("company_settings.add_dialog.error_false_companyid") + ", " + props.companyId
      );
      return;
    } else if (validateEmail(email) === false) {
      setErrorMessage(t("company_settings.add_dialog.error_email_not_valid"));
      return;
    }

    let selectedUserId = props.gridSelectionModel[0];

    const oldRole = props.usersData.filter(user => user.id === selectedUserId);

    const timeNow = moment.now();

    const timeUtc = moment.utc(timeNow, true);

    let endTimeUserDTO = {
      id: Number(selectedUserId),
      username: oldRole[0].userName,
      companyId: Number(props.companyId),
      companyRoleID: oldRole[0].companyRoleId,
      endDateTime: moment(timeUtc, true).format("YYYY-MM-DDTHH:mm:ss")
    }

    let updateUserDTO = {
        id: Number(selectedUserId),
        userName: email,
        companyId: Number(props.companyId),
        companyRoleId: Number(role.id),
        startDateTime: moment(timeUtc, true).format("YYYY-MM-DDTHH:mm:ss")
      };
    
      try {
        await dispatch(removeCompanyUser(endTimeUserDTO));
        await dispatch(addCompanyUser({ companyId: Number(props.companyId), user: updateUserDTO }));
      }
      catch (err) {
        console.log(err);
      }
      finally {
        dispatch(getCompanyUsers(Number(props.companyId)));
        props.handleSave();
      }
  };

  return (
    <Dialog open={true} onClose={handleClose}>
      <div className={styles["adduser-form-container"]}>
        {/**************************************************************************** */}

        <div className={styles["adduser-form-header"]}>
          {t("company_settings.add_dialog.add_new_user")}
        </div>
        {/* *************************************************************************** */}

        <TextField
          label={t("company_settings.add_dialog.user_email")}
          variant="standard"
          fullWidth
          required
          value={userEmail !== undefined ? userEmail : ""}
          type="email"
          onChange={event => setuserEmail(event.target.value)}
        />
        <TextField
          label={t("company_settings.add_dialog.user_role")}
          variant="standard"
          fullWidth
          required
          select
          value={selectedRole !== undefined ? selectedRole.id : ""}
          onChange={handleRoleChange}
        >
          {companyRoles.map(role => {
            return (
              <MenuItem key={"companyroles_" + role.id} value={role.id}>
                {role.name}
              </MenuItem>
            );
          })}
        </TextField>
        {/* *************************************************************************** */}

        <Button
          variant="contained"
          color="error"
          style={errorMessage !== "" ? { display: "" } : { display: "none" }}
        >
          {errorMessage}
        </Button>
        <Button variant="contained" onClick={props.add ? () => handleSave() : () => handleEditDialogSave(userEmail, selectedRole)}>
          {t("common.save")}
        </Button>
        <Button variant="contained" onClick={() => handleClose()}>
          {t("common.cancel")}
        </Button>
      </div>
    </Dialog>
  );
}
